import { InvoiceItem } from "@/api/opb/opbTypes";
import {
  flexRender,
  useReactTable,
  getCoreRowModel,
} from "@tanstack/react-table";
import { useMemo } from "react";
import { getColumns } from "./columns";

interface InvoiceItemsTableProps {
  items: InvoiceItem[];
  currencyCode?: string;
  className?: string;
  isMobile?: boolean;
}

export function InvoiceItemsTable({
  items,
  currencyCode,
  className,
  isMobile,
}: InvoiceItemsTableProps) {
  const columns = useMemo(
    () => getColumns({ currencyCode: currencyCode || "USD" }),
    [currencyCode]
  );

  const table = useReactTable({
    data: items,
    columns,
    state: {
      columnVisibility: {
        quantity: !isMobile, // hidden on mobile
        chargeDescription: !isMobile,
      },
    },
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <table className={`w-full ${className}`}>
      {!isMobile && (
        <thead className="bg-grey-95 " data-testid="invoice_items_table-header">
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <th
                  key={header.id}
                  className="text-left p-2 px-3 text-grey-30 font-light text-base"
                >
                  {header.isPlaceholder
                    ? null
                    : flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                </th>
              ))}
            </tr>
          ))}
        </thead>
      )}
      <tbody>
        {isMobile ? (
          <>
            {table.getRowModel().rows.map((row, index) => (
              <tr
                key={row.id}
                className="border-b border-grey-60"
                data-testid={`invoice_items_table-row-${index}-tr`}
              >
                <td className="py-3 px-4">
                  {row.getVisibleCells().map((cell) => (
                    <div key={cell.id} className="text-base">
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </div>
                  ))}
                </td>
              </tr>
            ))}
          </>
        ) : (
          <>
            {table.getRowModel().rows.map((row, index) => (
              <tr
                key={row.id}
                className="border-b border-grey-60"
                data-testid={`invoice_items_table-row-${index}-tr`}
              >
                {row.getVisibleCells().map((cell) => (
                  <td key={cell.id} className="text-base p-2 px-3">
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}
          </>
        )}
      </tbody>
    </table>
  );
}
